// ============================================================================
// CONECTA — a ponte: STATE do gerador → objeto `manual` do ManualLeitor
// ============================================================================
// A newsletter é autorada no Gerador do Conecta (Marketing), que salva um STATE
// estruturado (capa, comunicados, pessoas). A Biblioteca a lê no MESMO leitor
// dos manuais (ManualLeitor: índice, busca, link por capítulo). Esta função é a
// tradução entre os dois formatos — e é PURA de propósito: toda a lógica de risco
// (quebra de prosa em parágrafos, bullets → lista, prioridade → nível de aviso)
// mora aqui, testada em `mapearConecta.test.js`, e o resto é só desenho.
//
// A regra da ponte (DEM-00022, card do PLANO):
//   comunicado → 1 capítulo:  assunto→título · prioridade→aviso · publico+atuacao
//     →tabela · resumo→paragrafo · passosTitulo→secao + passos→passo · contatos→nota
//   pessoa → 1 capítulo:  "tipo — nome"→título · emoji+subtitulo+texto→paragrafo
//     · foto→imagens · mensagemFinal→paragrafo
//
// ⚠️ NOMES DE IMAGEM derivados da POSIÇÃO (`c<comunicado>-<passo>.webp`,
//    `p<pessoa>.webp`), não do valor guardado em `imagem`/`foto`. É o mesmo
//    esquema que o validador do backend (`conecta-acervo.js › arquivoValido`) e a
//    externalização da Fatia 2 têm de usar — os três lados casam pela posição.
//    Assim o mapeamento não depende de a Fatia 2 já ter rodado: a edição demo
//    renderiza com as imagens só faltando (404 → "indisponível"), não quebrada.
// ============================================================================

// Prioridade do comunicado → nível do bloco `aviso`. ALTA é um "não ignore"
// (vermelho), MEDIA um "cuidado" (âmbar-forte), BAIXA um "leia" (âmbar-suave).
// Valor desconhecido cai em `atencao` (falha para o lado visível, não some).
export function nivelDaPrioridade(prioridade) {
  switch (String(prioridade || '').toUpperCase()) {
    case 'ALTA': return 'critico';
    case 'MEDIA': return 'atencao';
    case 'BAIXA': return 'importante';
    default: return 'atencao';
  }
}

const ROTULO_PRIORIDADE = { critico: 'alta', atencao: 'média', importante: 'baixa' };

// Prosa (textarea) → parágrafos. Separador é a LINHA EM BRANCO (`\n\n`); quebras
// simples ficam dentro do parágrafo (o bloco `paragrafo` usa `white-space:
// pre-line`, então bullets e versos de mensagem preservam a quebra na tela).
export function quebrarParagrafos(texto) {
  if (!texto) return [];
  return String(texto).split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

// Descrição de um passo → { texto, itens }. Linha que começa com bullet (`•`, `-`
// ou `*`) vira item de lista; o resto é o texto do passo. É o que transforma a
// lista embutida na descrição em lista de verdade, sem markdown. (O `*` casa com a
// arte do Gerador, onde "* " no início da linha também vira tópico.)
const BULLET_RE = /^[-•*]\s+(.*)$/;
export function parsearPassoDescricao(descricao) {
  const itens = [];
  const linhasTexto = [];
  for (const linha of String(descricao || '').split('\n')) {
    const t = linha.trim();
    if (!t) continue;
    const m = BULLET_RE.exec(t);
    if (m) itens.push(m[1].trim());
    else linhasTexto.push(t);
  }
  return { texto: linhasTexto.join('\n'), itens };
}

// Ano a partir da data ISO do STATE ("2026-09-18" → "2026").
function anoDaData(data) {
  const m = /^(\d{4})/.exec(String(data || ''));
  return m ? m[1] : String(new Date().getFullYear());
}

/**
 * Os BLOCOS de um comunicado, sem o embrulho de capítulo. É o miolo reutilizável:
 * `capituloDeComunicado` embrulha isto num capítulo (1 comunicado = 1 capítulo, na
 * leitura de UMA edição); o Manual do Portal (`montarManualPortal.js`) usa os
 * blocos direto, agrupando N comunicados de N edições sob o capítulo do MÓDULO.
 *
 * ⚠️ `prefixoImagem` resolve a imagem cross-edição. Numa edição só, o nome basta
 *    (`c<i>-<j>.webp`), porque o token/baseUrl já aponta para a pasta da edição.
 *    No Manual do Portal os comunicados vêm de CHAVEs diferentes e o leitor usa UM
 *    baseUrl só — então o nome carrega a CHAVE (`<chave>/c<i>-<j>.webp`), que o
 *    proxy `/portal/img/:chave/:arquivo` separa. O `indice` continua sendo a
 *    POSIÇÃO do comunicado na SUA edição (é o que nomeou a imagem no publish).
 */
export function blocosDeComunicado(com, indice, { prefixoImagem = '' } = {}) {
  const blocos = [];

  // Prioridade → um aviso no topo (a cor sinaliza a urgência de relance).
  const nivel = nivelDaPrioridade(com.prioridade);
  blocos.push({ tipo: 'aviso', nivel, titulo: `Prioridade ${ROTULO_PRIORIDADE[nivel]}` });

  // Público + Atuação → uma tabela curta (a ficha do leitor tem rótulos fixos
  // que não servem aqui — "Responsável"/"Objetivo" ≠ "Público"/"Atuação").
  const publico = (com.publico || []).join(', ');
  const atuacao = (com.atuacao || []).join(', ');
  if (publico || atuacao) {
    blocos.push({ tipo: 'tabela', colunas: ['Público', 'Atuação'], linhas: [[publico || '—', atuacao || '—']] });
  }

  // Resumo → prosa.
  const resumo = quebrarParagrafos(com.resumo);
  if (resumo.length) blocos.push({ tipo: 'paragrafo', paragrafos: resumo });

  // Passos → uma seção + um bloco `passo` por passo.
  const passos = com.passos || [];
  if (passos.length) {
    if (com.passosTitulo) blocos.push({ tipo: 'secao', titulo: com.passosTitulo });
    passos.forEach((passo, j) => {
      const { texto, itens } = parsearPassoDescricao(passo.descricao);
      const bloco = { tipo: 'passo' };
      if (passo.titulo) bloco.titulo = passo.titulo;
      if (texto) bloco.texto = texto;
      if (itens.length) bloco.itens = itens;
      // Nome derivado da posição — casa com o backend e a Fatia 2.
      if (passo.imagem) bloco.imagens = [{ arquivo: `${prefixoImagem}c${indice}-${j}.webp` }];
      blocos.push(bloco);
    });
  }

  // Contatos → uma nota com a lista "Nome — Cargo — e-mail".
  const contatos = com.contatos || [];
  if (contatos.length) {
    blocos.push({
      tipo: 'nota',
      icone: 'anexo',
      texto: 'Em caso de dúvidas, fale com:',
      itens: contatos.map((c) => [c.nome, c.cargo, c.email].filter(Boolean).join(' — ')),
    });
  }

  return blocos;
}

// Um comunicado vira um capítulo (leitura de UMA edição).
function capituloDeComunicado(com, indice, numero) {
  return {
    numero,
    id: `com-${indice}`,
    titulo: com.assunto || `Comunicado ${numero}`,
    blocos: blocosDeComunicado(com, indice),
  };
}

// Uma pessoa (promoção, boas-vindas…) vira um capítulo.
function capituloDePessoa(pes, indice, numero) {
  const blocos = [];

  // Foto no topo (nome derivado da posição — casa com o backend e a Fatia 2).
  if (pes.foto) {
    blocos.push({ tipo: 'imagens', imagens: [{ arquivo: `p${indice}.webp`, legenda: pes.nome || '' }] });
  }

  // Emoji + subtítulo como abertura, depois o texto.
  const abertura = [pes.emoji, pes.subtitulo].filter(Boolean).join(' ').trim();
  const paragrafos = [abertura, ...quebrarParagrafos(pes.texto)].filter(Boolean);
  if (paragrafos.length) blocos.push({ tipo: 'paragrafo', paragrafos });

  // Mensagem final destacada.
  const fim = quebrarParagrafos(pes.mensagemFinal);
  if (fim.length) blocos.push({ tipo: 'paragrafo', paragrafos: fim });

  const titulo = [pes.tipo, pes.nome].filter(Boolean).join(' — ');
  return { numero, id: `pes-${indice}`, titulo: titulo || `Pessoa ${numero}`, blocos };
}

/**
 * Traduz o STATE do gerador do Conecta para o objeto `manual` que o ManualLeitor
 * consome. `chave` é a `conecta-000X` da edição (vira `manual.id`).
 *
 * Comunicados primeiro, pessoas depois; a numeração dos capítulos é contínua.
 */
export function mapearConecta(state, chave) {
  const s = state || {};
  const capa = s.capa || {};

  const comunicados = (s.comunicados || []).map((c, i) => capituloDeComunicado(c, i, i + 1));
  const pessoas = (s.pessoas || []).map((p, i) => capituloDePessoa(p, i, comunicados.length + i + 1));

  return {
    id: chave,
    titulo: capa.titulo || `Conecta — Edição ${s.edicao ?? ''}`.trim(),
    finalidade: capa.texto || '',
    // "Versão" no leitor = número da edição; a data dá o ano.
    versao: String(s.edicao ?? ''),
    ano: anoDaData(s.data),
    aplicacao: 'Newsletter interna',
    capitulos: [...comunicados, ...pessoas],
  };
}

export default mapearConecta;
